import type React from "react";

import { useEffect, useId, useRef, useState } from "react";
import { Search, X, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { UseMutationResult } from "@tanstack/react-query";
import type GenericResponse from "@/model/generic-response";

//T=Type Objct V = Type of variables
type SearchAutocompleteProps<T, V> = {
  label?: string;
  placeholder?: string;
  placeholderIcon?: React.ComponentType;
  value?: string;
  additionalParams?: V;
  onSearch?: (keyword: string) => void;
  onSelect?: (result: T) => void;
  onClear?: () => void;
  queryFunction: UseMutationResult<GenericResponse<T>, Error, V>;
  formatResult: (result: T, index: number) => React.ReactNode;
  className?: string;
  autoFocus?: boolean;
  minSearchLength?: number;
  debounceTime?: number;
};

export default function SearchAutocomplete<T, V>({
  label,
  placeholder = "Search",
  placeholderIcon: PlaceholderIcon = Search,
  value,
  additionalParams,
  onSearch,
  onSelect,
  onClear,
  queryFunction,
  formatResult,
  className: _className,
  autoFocus = false,
  minSearchLength = 3,
  debounceTime = 3000,
}: SearchAutocompleteProps<T, V>) {
  // Stable, collision-free id so the optional <Label> can be associated with the field
  // (3.3.2 Labels or Instructions — A). This component renders several times per page, so
  // a hard-coded id would produce duplicates (4.1.1 Parsing — A).
  const inputId = useId();
  const [searchString, setSearchString] = useState<string>(value ?? "");
  const [results, setResults] = useState<T[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<{ hasNext: boolean; nextUrl: string }>(
    { hasNext: false, nextUrl: "" },
  );
  const [, setIsFocused] = useState(autoFocus);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchString(value ?? "");
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    setResults([]);
    onSearch?.(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length >= minSearchLength) {
      const params = additionalParams
        ? {
            ...additionalParams,
            queryString: value,
          }
        : { queryString: value };
      searchTimeoutRef.current = setTimeout(() => {
        queryFunction.mutateAsync(params as V, {
          onSuccess: (data: GenericResponse<T>) => {
            if (data.data) {
              const newResults: T[] = data.data || [];
              setResults((prevResults) => [...prevResults, ...newResults]);
              setHasMore({
                hasNext: data.next !== "" ? true : false,
                nextUrl: data.next !== "" ? data.next! : "",
              });
              setShowResults(true);
            }
          },
        });
      }, debounceTime);
    } else {
      setResults([]);
      setShowResults(false);
    }
  };

  const handleClear = () => {
    setSearchString("");
    setResults([]);

    setShowResults(false);
    onClear?.();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleSelectItem = (result: T) => {
    setShowResults(false);
    onSelect?.(result);
  };

  const handleLoadMore = () => {
    queryFunction.mutateAsync(
      {
        ...additionalParams,
        queryString: searchString,
        url: hasMore.nextUrl,
      } as V,
      {
        onSuccess: (data: GenericResponse<T>) => {
          if (data.data) {
            const newResults: T[] = data.data || [];
            setResults((prevResults) => [...prevResults, ...newResults]);
            setHasMore({
              hasNext: data.next !== "" ? true : false,
              nextUrl: data.next !== "" ? data.next! : "",
            });
          }
        },
        onError: (error: Error) => {
          console.error("Error loading more results:", error);
        },
      },
    );
  };

  return (
    <div className="space-y-2" ref={componentRef}>
      {/* 3.3.2 Labels or Instructions — A: the <Label> had no htmlFor and the field no id,
          so the label was orphaned and the search box unnamed. Where no visible label is
          supplied, the placeholder is promoted to the accessible name. */}
      {label && (
        <Label htmlFor={inputId} className="text-smtext-md font-semibold">
          {label}
        </Label>
      )}
      <div className="relative">
        <div className="relative">
          <div className="relative">
            <Input
              id={inputId}
              aria-label={label ? undefined : (placeholder ?? "Search")}
              type="text"
              value={searchString}
              onChange={handleInputChange}
              placeholder={placeholder}
              autoFocus={autoFocus}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="bottom-0 pr-10 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {queryFunction.isPending ? (
                <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
              ) : searchString ? (
                // 2.1.1 Keyboard — A / 4.1.2 — A: the clear control was a bare Lucide
                // <svg> with an onClick — not focusable, no role, no name.
                <button
                  type="button"
                  onClick={handleClear}
                  aria-label="Clear search"
                  className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <X
                    aria-hidden="true"
                    className="size-4 cursor-pointer text-muted-foreground hover:text-foreground"
                  />
                </button>
              ) : (
                <div className="flex size-4 items-center justify-center text-muted-foreground">
                  <PlaceholderIcon />
                </div>
              )}
            </div>
          </div>
        </div>

        {showResults && results.length > 0 && (
          <Card className="absolute z-20 mt-2 w-full overflow-hidden">
            <CardContent className="p-0">
              <ul className="max-h-[500px] overflow-y-auto">
                {/* 2.1.1 Keyboard — A / 4.1.2 — A: results were <li onClick>, so a
                    keyboard user could type a query but could not select any result. */}
                {results.map((result, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      className={`w-full cursor-pointer px-4 py-2 text-left hover:bg-primary-extraLight hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${index % 2 ? "bg-gray-100" : "bg-white"}`}
                      onClick={() => handleSelectItem(result)}
                    >
                      {formatResult?.(result, index)}
                    </button>
                  </li>
                ))}
                {hasMore.hasNext && (
                  <li className="flex justify-center border-t p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleLoadMore}
                      disabled={queryFunction.isPending}
                      className="w-full"
                    >
                      {queryFunction.isPending ? (
                        <span className="flex items-center justify-center">
                          <LoaderCircle className="mr-2 size-4 animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        "Show More"
                      )}
                    </Button>
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        )}

        {showResults &&
          searchString.length >= minSearchLength &&
          results.length === 0 &&
          !queryFunction.isPending && (
            <Card className="absolute top-[40px] z-20 mt-1 w-full">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  No results found
                </p>
              </CardContent>
            </Card>
          )}
      </div>
    </div>
  );
}
